import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import RosterRxIntro from '../components/RosterRxIntro';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';
import { buildAboutPageSchema, toJsonLd } from '../schema';

const ABOUT_TITLE = 'About RosterRx — Features & Release Notes';
const ABOUT_DESCRIPTION =
  'How RosterRx works: rankings aggregation, live draft mode, AI pick suggestions, mock drafts, and season-long lineup and trade tools, from pre-draft through playoffs.';
export const ABOUT_PAGE_SCHEMA = buildAboutPageSchema({
  path: '/about',
  title: ABOUT_TITLE,
  description: ABOUT_DESCRIPTION,
});

const RELEASE_NOTES = [
  {
    label: 'September 2026',
    bullets: [
      'Start/Sit and Trade Analysis now factor in each player\'s recent performance trend and latest news, not just season-long rankings (Pro)',
      'Trade Analysis now rates the other manager\'s side of a trade against their real roster — pick their players from a list instead of searching, and edit their roster in place if it\'s changed (Pro)',
      'Playoff Outlook is back as Matchup Preview — a rolling next-4-week schedule read available every week in-season, not just weeks 14-17 (Pro)',
    ],
  },
  {
    label: 'August 2026',
    bullets: [
      'Commissioner Mode — host a live multiplayer draft with friends: share a join code or QR, they join as a guest with no account needed, and you set draft order and an optional pick timer (Pro)',
      'Generate an AI scouting profile for a manager before a draft, or view their saved one anytime (Pro)',
      'Fixed an issue where AI picks in Mock Draft could intermittently fail after being away from an active draft',
    ],
  },
  {
    label: 'July 2026',
    bullets: [
      'Mock Draft now has its own dedicated button on the Draft Hub, and the AI starts picking immediately even when an opponent holds the very first pick',
      'Scoring Rules and Roster Requirements can now be edited directly from their own cards, just like your roster',
      'Yahoo league import now offers to reuse your already-connected account instead of asking you to reconnect every time',
      '"Draft Mode" renamed to "My Drafts" throughout the app',
      'Verification emails and the support contact now come from roster-rx.com',
    ],
  },
  {
    label: 'June 2026',
    bullets: [
      'New user sign-in after email confirmation now works automatically — no second login step required',
      'CSV export High/Low columns now correctly show overall rank spread across sources',
      'CSV upload button is now disabled for anonymous users with a sign-in prompt',
    ],
  },
  {
    label: 'May 2026',
    bullets: [
      'AI is now live — all Pro features powered by Claude, no longer in demo mode',
      'Start/Sit, Playoff Outlook, and Trade Analysis are in early access — real AI using schedule and defensive rank data; midseason stats coming in a future update',
      'Mock Draft AI picks now use real Claude simulations',
    ],
  },
  {
    label: 'March 2026',
    bullets: [
      'Manager profiles — track who you play against across drafts and leagues',
      'AI-powered draft pick suggestions (Pro)',
      'Start/Sit recommendations with matchup strength context (Pro)',
    ],
  },
  {
    label: 'February 2026',
    bullets: [
      'Live snake draft with real-time pick tracking',
      'League mode — import your Yahoo league and view your roster',
      'ESPN and Yahoo rankings auto-scraped weekly',
    ],
  },
];

/**
 * Static About page with personal bio and collapsible release notes.
 */
function AboutView() {
  const [showOlder, setShowOlder] = useState(false);
  usePrerenderReady();

  return (
    <div>
      <Helmet>
        <title>{ABOUT_TITLE}</title>
        <meta name='description' content={ABOUT_DESCRIPTION} />
        <link rel='canonical' href={`${SITE_URL}/about`} />
        <meta property='og:title' content={ABOUT_TITLE} />
        <meta property='og:description' content={ABOUT_DESCRIPTION} />
        <meta property='og:url' content={`${SITE_URL}/about`} />
        <meta property='og:type' content='website' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content={ABOUT_TITLE} />
        <meta name='twitter:description' content={ABOUT_DESCRIPTION} />
        <script type='application/ld+json'>{toJsonLd(ABOUT_PAGE_SCHEMA)}</script>
      </Helmet>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>About RosterRx</h2>
        <RosterRxIntro />
        <p>
          Pro features are live. A $5/month subscription covers server costs and keeps development
          moving — no markups, no upsells. All Pro features are fully functional today; sign up for
          free and the in-app upgrade prompt will be available soon.
        </p>
        <p>
          Have feedback, a feature idea, or just want to say hi?{' '}
          <a href='mailto:support@roster-rx.com'>support@roster-rx.com</a>
        </p>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>Features</h2>
        <p style={{ marginBottom: 16, color: 'var(--text-muted)' }}>
          RosterRx follows your season from preparation through the draft and into weekly lineup decisions.
          Core features are free — Pro unlocks AI tools for{' '}
          <strong>$5/month</strong>. That covers server costs and the time I put into this; no markups, no upsells.
        </p>

        <FeaturePhase
          phase='Pre-Draft'
          description='Build your rankings before draft day.'
          features={[
            { label: 'Aggregated expert rankings from ESPN & Yahoo', pro: false },
            { label: 'Consensus rankings across all sources', pro: false },
            { label: 'CSV import & export for custom rankings', pro: false },
            { label: 'Scoring format filters (PPR, Half, Standard)', pro: false },
          ]}
        />

        <FeaturePhase
          phase='Mock Draft'
          description='Practice your draft strategy before it counts.'
          features={[
            { label: "AI simulates every other team's picks in a full snake draft for realistic practice", pro: true },
          ]}
        />

        <FeaturePhase
          phase='Draft'
          description='Run your live snake draft. Pro unlocks real-time AI guidance.'
          features={[
            { label: 'Live snake draft with real-time pick tracking', pro: false },
            { label: 'AI pick suggestion: best available for your roster', pro: true },
            { label: 'Round prediction: see what other teams are likely to draft next', pro: true },
            { label: 'Commissioner Mode: host a live multiplayer draft room via join code or QR — friends join as guests with no account required', pro: true },
          ]}
        />

        <FeaturePhase
          phase='Managers'
          description={"Import your league's history season to season — from Yahoo or ESPN, which also pulls in each opponent's full draft history along with the league, or start fresh with a scratch-created league or draft — and link every opponent you play across drafts and seasons to build their profile."}
          features={[
            { label: 'Create and manage manager profiles (name, nickname, notes)', pro: false },
            { label: 'Link managers to draft slots and league teams', pro: false },
            { label: 'View per-manager draft history with pick breakdowns', pro: false },
            { label: 'View per-manager league history across seasons', pro: false },
            { label: "Round Prediction already factors in an opponent's draft history and tendencies", pro: true },
            { label: 'Generate an AI scouting profile for a manager before a draft, or view their saved one anytime', pro: true },
          ]}
        />

        <FeaturePhase
          phase='Season'
          description='Manage your roster week to week with data-driven decisions.'
          features={[
            { label: 'League mode: import your Yahoo league and view your roster', pro: false },
            { label: 'Start/Sit recommendations with matchup strength context', pro: true },
            { label: 'Matchup Preview — rolling schedule-difficulty read for any player on your roster', pro: true },
            { label: 'Trade analysis with GREAT / GOOD / POOR ratings', pro: true },
          ]}
        />

        <p style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
          <span style={{ fontWeight: 600, color: 'var(--accent)' }}>Free</span> — no account required for rankings.{' '}
          <span style={{ fontWeight: 600 }}>Pro ($5/mo)</span> — all AI features.
        </p>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>What's New</h2>

        <ReleaseEntry entry={RELEASE_NOTES[0]} />

        {RELEASE_NOTES.length > 1 && showOlder && (
          RELEASE_NOTES.slice(1).map((entry) => (
            <ReleaseEntry key={entry.label} entry={entry} />
          ))
        )}

        {RELEASE_NOTES.length > 1 && (
          <button
            className='btn btn-secondary'
            style={{ marginTop: 8 }}
            onClick={() => setShowOlder(o => !o)}
          >
            {showOlder ? 'Hide older releases' : 'Show older releases'}
          </button>
        )}
      </div>
    </div>
  );
}

function FeaturePhase({ phase, description, features }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontWeight: 700, marginBottom: 2 }}>{phase}</p>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-muted)' }}>{description}</p>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {features.map((f) => (
          <li key={f.label} style={{ marginBottom: 4 }}>
            {f.label}
            {f.pro && (
              <span style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--accent)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}>Pro</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReleaseEntry({ entry }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>{entry.label}</p>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {entry.bullets.map((bullet) => (
          <li key={bullet} style={{ marginBottom: 4 }}>{bullet}</li>
        ))}
      </ul>
    </div>
  );
}

export default AboutView;